import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { judgeInvitationService } from '../../services/supabaseService';
import './LoginNew.css';

const JudgeRegistration = ({ onRegistrationSuccess, onBackToLogin }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [step, setStep] = useState('invitation'); // 'invitation' | 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [validatedInvitation, setValidatedInvitation] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    address: ''
  });

  // ============ STEP 1: Validar Código de Invitación ============
  const handleValidateInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!invitationCode.trim()) {
      setError('Por favor ingresa el código de invitación');
      setLoading(false);
      return;
    }

    try {
      const result = await judgeInvitationService.validateInvitation(invitationCode.trim());
      
      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      setValidatedInvitation(result.data);
      setStep('form');
    } catch (err) {
      console.error('Error validando invitación:', err);
      setError('Error al validar el código');
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 2: Validar y Registrar ============
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('El nombre es obligatorio');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('El correo es obligatorio');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor ingresa un correo válido');
      return false;
    }
    
    if (!formData.password) {
      setError('La contraseña es obligatoria');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    
    if (formData.password !== formData.passwordConfirm) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    
    return true;
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const { data: authDataWithProfile, error: authErrorWithProfile } = await signUp(
        formData.email.trim(),
        formData.password,
        'judge',
        {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          address: formData.address.trim() || null,
          is_active: true,
        }
      );

      if (authErrorWithProfile) {
        setError(authErrorWithProfile);
        setLoading(false);
        return;
      }

      const judgeData = authDataWithProfile?.profile;
      if (!judgeData) {
        setError('Error guardando el perfil del juez. Intenta nuevamente.');
        setLoading(false);
        return;
      }

      // 3. Marcar invitación como usada
      await judgeInvitationService.markInvitationAsUsed(
        validatedInvitation.id,
        judgeData.id
      );

      setStep('success');
      // Redirigir después de 3 segundos
      setTimeout(() => {
        if (onRegistrationSuccess) onRegistrationSuccess();
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error durante el registro:', err);
      setError('Error al registrar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 1: Código de Invitación ============
  if (step === 'invitation') {
    return (
      <div className="login-container">
        <div className="login-card">
          <button
            onClick={onBackToLogin}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '20px',
              color: '#8b5cf6',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <ArrowLeft size={16} />
            Volver al login
          </button>

          <div className="login-header">
            <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="login-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
            <h1>Registrarse como Juez</h1>
            <p>Necesitas un código de invitación del administrador</p>
          </div>

          <form onSubmit={handleValidateInvitation} className="login-form">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="invitationCode">Código de Invitación</label>
              <input
                id="invitationCode"
                type="text"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                placeholder="Ingresa el código que recibiste"
                disabled={loading}
                className="form-input"
                maxLength="20"
                style={{ 
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  fontSize: '18px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                El administrador te envió un código de 8 caracteres válido por 30 días
              </p>
            </div>

            <div style={{
              padding: '12px',
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#065f46',
              marginBottom: '16px'
            }}>
              <strong>💡 Consejo:</strong> Si aún no tienes código, contacta al administrador de la competencia
            </div>

            <button
              type="submit"
              disabled={loading}
              className="login-submit"
            >
              {loading ? 'Validando...' : 'Continuar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ============ STEP 2: Formulario de Registro ============
  if (step === 'form') {
    return (
      <div className="login-container">
        <div className="login-card">
          <button
            onClick={() => {
              setStep('invitation');
              setInvitationCode('');
              setError('');
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '20px',
              color: '#8b5cf6',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <ArrowLeft size={16} />
            Volver
          </button>

          <div className="login-header">
            <h1>Crear Cuenta</h1>
            <p>Completa tu información para registrarte</p>
          </div>

          <form onSubmit={handleRegistration} className="login-form">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="name">Nombre Completo *</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Tu nombre"
                disabled={loading}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Correo Electrónico *</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu@correo.com"
                disabled={loading}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña *</label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                disabled={loading}
                className="form-input"
                required
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Mínimo 6 caracteres
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="passwordConfirm">Confirmar Contraseña *</label>
              <input
                id="passwordConfirm"
                type="password"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleInputChange}
                placeholder="••••••••"
                disabled={loading}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Teléfono (opcional)</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1234567890"
                disabled={loading}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Dirección (opcional)</label>
              <input
                id="address"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Tu dirección"
                disabled={loading}
                className="form-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="login-submit"
            >
              {loading ? 'Registrando...' : 'Crear Cuenta'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ============ STEP 3: Éxito ============
  if (step === 'success') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <CheckCircle size={48} color="#10b981" style={{ marginBottom: '16px' }} />
            <h1>¡Registro Exitoso!</h1>
            <p>Tu cuenta ha sido creada correctamente</p>
          </div>

          <div style={{
            padding: '20px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            borderLeft: '4px solid #10b981',
            marginBottom: '20px'
          }}>
            <p style={{ color: '#166534', marginBottom: '10px' }}>
              ✅ Tu cuenta está lista para usar
            </p>
            <p style={{ color: '#166534', fontSize: '14px' }}>
              Ahora puedes iniciar sesión con tu correo y contraseña para acceder a las competencias en las que estés habilitado.
            </p>
          </div>

          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
            <p>Redirigiendo en 3 segundos...</p>
          </div>
        </div>
      </div>
    );
  }
};

export default JudgeRegistration;
